import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractApi } from '@/features/contracts/api/contractApi';
import { notifications } from '@/utils/notifications';
import { formatCents, REFUND_STATUS_LABELS } from '@/features/contracts/utils/refundLabels';
import type { ContractFilters, CreateContractDto } from '@/features/contracts/types/contract.types';

/**
 * Hook React Query partagé pour la liste des contrats
 */
export const useContracts = (filters?: ContractFilters) => {
  return useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => contractApi.getContracts(filters),
    staleTime: 30000,
    refetchInterval: 120_000,
  });
};

/**
 * Annuler un contrat (admin) — annule signature YouSign et paiement Stripe
 */
export const useCancelContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      skipAvoir,
      refund,
      refundCommission,
    }: {
      id: number;
      skipAvoir?: boolean;
      refund?: boolean;
      refundCommission?: boolean;
    }) => contractApi.cancelContract(id, { skipAvoir, refund, refundCommission }),
    onSuccess: ({ refund }, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['contract-refunds', id] });
      notifications.success(
        refund
          ? `Contrat annulé — remboursement de ${formatCents(refund.amountCents)} : ${REFUND_STATUS_LABELS[refund.status].toLowerCase()}`
          : 'Contrat annulé avec succès',
      );
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'annulation du contrat';
      notifications.error(message);
    },
  });
};

/**
 * Offrir un contrat — crée un avoir (IS_AVOIR = false)
 */
export const useOfferContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => contractApi.offerContract(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      queryClient.invalidateQueries({ queryKey: ['contract-offered', id] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      notifications.success('Contrat offert avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'offre du contrat';
      notifications.error(message);
    },
  });
};

/**
 * Vérifier si un contrat a été offert
 */
export const useIsContractOffered = (id?: number) => {
  return useQuery({
    queryKey: ['contract-offered', id],
    queryFn: () => contractApi.isContractOffered(id!),
    enabled: !!id,
  });
};

/**
 * Modifier les informations bénéficiaire et véhicule d'un contrat (INTERNAL only)
 */
export const useEditContractInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: {
      beneficiary?: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string };
      vehicle?: { registration?: string; kmStart?: string; vehicleSellingPrice?: string };
      regeneratePdf?: boolean;
    } }) => contractApi.editContractInfo(id, data),
    onSuccess: (result, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      queryClient.invalidateQueries({ queryKey: ['contract-pdf', id] });
      if (result?.signatureResend === 'failed') {
        notifications.warning(
          'Informations modifiées, mais la nouvelle demande de signature n’a pas pu être envoyée au bénéficiaire. Relancez-la depuis la fiche contrat.',
        );
      } else if (result?.signatureResend === 'resent') {
        notifications.success('Informations modifiées. Une nouvelle demande de signature a été envoyée au bénéficiaire.');
      } else {
        notifications.success('Informations modifiées avec succès');
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la modification';
      notifications.error(message);
    },
  });
};

/**
 * Demander l'annulation d'un contrat (prescripteur)
 */
export const useRequestContractCancellation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, details }: { id: number; reason: string; details?: string }) =>
      contractApi.requestCancellation(id, reason, details),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', variables.id] });
      notifications.success('Demande d\'annulation enregistrée avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la demande d\'annulation';
      notifications.error(message);
    },
  });
};

/**
 * Aperçu de l'email « infos de connexion pour déclarer une panne »
 */
export const useSavAccessEmailPreview = (id: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['contract', id, 'sav-access-email-preview'],
    queryFn: () => contractApi.getSavAccessEmailPreview(id),
    enabled,
    staleTime: 0,
  });
};

/**
 * Envoyer au bénéficiaire ses infos de connexion pour déclarer une panne
 */
export const useSendSavAccessEmail = () => {
  return useMutation({
    mutationFn: (id: number) => contractApi.sendSavAccessEmail(id),
    onSuccess: () => {
      notifications.success('Email envoyé au client');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || "Erreur lors de l'envoi de l'email";
      notifications.error(message);
    },
  });
};

/**
 * Créer un contrat
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contract: CreateContractDto) => contractApi.createContract(contract),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      notifications.success('Contrat créé avec succès');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la création du contrat';
      notifications.error(message);
    },
  });
};

/**
 * Dernier paiement remboursable d'un contrat (modale d'annulation)
 */
/**
 * Rembourser le dernier paiement d'un contrat déjà annulé (admin). Un prélèvement
 * encore en cours est remboursé dès son encaissement.
 */
export const useRefundCancelledContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, refundCommission }: { id: number; refundCommission: boolean }) =>
      contractApi.refundCancelledContract(id, { refundCommission }),
    onSuccess: ({ refund }, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      queryClient.invalidateQueries({ queryKey: ['contract-refunds', id] });
      queryClient.invalidateQueries({ queryKey: ['contract-refundable-payment', id] });
      notifications.success(
        `Remboursement de ${formatCents(refund.amountCents)} : ${REFUND_STATUS_LABELS[refund.status].toLowerCase()}`,
      );
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la demande de remboursement';
      notifications.error(message);
    },
  });
};

export const useRefundablePayment = (contractId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['contract-refundable-payment', contractId],
    queryFn: () => contractApi.getRefundablePayment(contractId),
    enabled,
    staleTime: 0,
  });
};

/**
 * Demandes de remboursement client d'un contrat (fiche contrat)
 */
export const useContractRefunds = (contractId: number | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['contract-refunds', contractId],
    queryFn: () => contractApi.getContractRefunds(contractId as number),
    enabled: enabled && !!contractId,
  });
};
